import { Select, TabsProps, Tooltip } from "antd";
import cn from "classnames";
import { FC, Key, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isDesktop, isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SoundOutlined, MutedOutlined } from '@ant-design/icons';
import { ReactSVG } from "react-svg";

import {
  getCurrentRaceLength,
  getExpiredGameMissionData,
  /*getGameBoxRaffleTickets,*/
  getGameBoxView,
  getIsButtonVisible,
  getMyBonusesData,
  getSelectedGameBoxTag,
  getThemeSwitcher,
} from "../../store/selectors";
import { requestExpiredProgressiveMissions } from "../../store/actionCreators/expiredProgressiveMissions";

import { changeTheme } from "../../utils/changeTheme";
import BonusCard from "../BonusCard";
import Categories from "../Categories";
import EmptyPage from "../EmptyPage";
import Loader from "../Loader";
import Missions from "../Missions";
import MissionTitleAndDescriptionWidget from "../MissionTitleAndDescriptionWidget";
import Version from "../Version";

import BonusCategories from "./components/BonusCategories";
import RaceBonus from "./components/RaceBonus";
import MiniGames from "./components/MiniGames";
import TabsSwitcher from "../common/TabsSwitcher";

import { getSubmissions } from "../../helpers/helpers";
import { changeGameBoxView } from "../../store/actionCreators/ui";
import { requestMiniGames } from "../../store/actionCreators/miniGames";
import "./styles.scss";

interface MyBonusesProps {
  isLandscape: boolean;
  storedRaceValue?: string | null;
}

const MyBonuses: FC<MyBonusesProps> = ({
  isLandscape,
  storedRaceValue,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const missionData = useSelector(getMyBonusesData);
  const expiredMissionData = useSelector(getExpiredGameMissionData);
  const raceLength = useSelector(getCurrentRaceLength);
  const gameBoxType = useSelector(getSelectedGameBoxTag);
/*  const raffleTickets = useSelector(getGameBoxRaffleTickets);*/
  const gameBoxView = useSelector(getGameBoxView);
  const isSwitcherVisible = useSelector(getThemeSwitcher) === "true";
  const isButtonVisible = useSelector(getIsButtonVisible) !== "hideButton";
  const [loader, setLoader] = useState<boolean>(true);
  const isWidget = gameBoxView === "widget";
  const [isOpen, setIsOpen] = useState<boolean | undefined>(false);
  const closeWidgetAudio = useMemo(() => new Audio('static/sounds/gb_close.mp3'), []);
  const openWidgetAudio = useMemo(() => new Audio('static/sounds/gb_open.mp3'), []);
  const bgWidgetAudioRef = useRef(new Audio('static/sounds/gb_bg.mp3'));
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const elementMobileHeaderRef = useRef<HTMLDivElement | null>(null);

  const close = () => {
    setLoader(true);
    dispatch(changeGameBoxView(''));
    closeWidgetAudio.play().catch(console.error);
    const bgWidgetAudio = bgWidgetAudioRef.current;
    openWidgetAudio.pause();
    openWidgetAudio.currentTime = 0;
    bgWidgetAudio.pause();
    bgWidgetAudio.currentTime = 0;
    window.parent.postMessage({ type: "CLOSE_GAME_BOX_GLOBAL_WIDGET" }, "*");
  };

  useEffect(() => {
    if (isWidget) {
      dispatch(requestMiniGames());
      const bgWidgetAudio = bgWidgetAudioRef.current;
      bgWidgetAudio.loop = true;
      openWidgetAudio.play().catch(console.error);
      openWidgetAudio.onended = () => {
        // default mute
        bgWidgetAudio.muted = isMuted;
        bgWidgetAudio.play().catch(console.error);
      };
      setTimeout(() => {
        setLoader(false);
      }, 1500);
    }
  }, [isWidget]);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    bgWidgetAudioRef.current.muted = !isMuted;
  };

  const classes = cn("my-bonuses", {
    [gameBoxType]: gameBoxType,
    mobile: isMobile,
    "landscape-missions": isLandscape && isMobile,
  });

  const categories = Object.keys(missionData).map((tag) => ({
    tag,
    isActive: !!missionData[tag]?.length,
    activeMissionsCount: missionData[tag]?.filter(
      (m: { status: string }) => !["completed", "failed"].includes(m.status)
    ).length,
  }));

  const sortedCategories = categories.sort(
    (a, b) => Number(b.isActive) - Number(a.isActive)
  );

  const cardsData = useCallback((missions: { [x: string]: [] }) => {
    let content;

    if (
      missions?.[gameBoxType]?.length &&
      gameBoxType !== "race" &&
      gameBoxType !== "miniGames" &&
      gameBoxType !== "raffle"
    ) {
      const { emptySubmissions, nonEmptySubmissions } = getSubmissions(
          gameBoxType,
          missions
      );

      const cardsDataWithSubmissions = nonEmptySubmissions?.map(
        (data: { submissions: string[]; id: Key }, index: Key) => (
          <Missions
            key={index}
            data={data}
            onClose={close}
            isLandscape={isLandscape}
            mobileHeader={elementMobileHeaderRef.current}
          />
        )
      );
      const cardsDataWithoutSubmissions = emptySubmissions
        ?.map((data: Record<string, string>, index: Key) => (
          <BonusCard
            key={index}
            data={data}
            onClose={close}
            isWidget={isWidget}
            bgWidgetAudio={bgWidgetAudioRef.current}
          />
        ))
        ?.filter(Boolean);

      content = (
        <>
          {cardsDataWithSubmissions}
          {cardsDataWithoutSubmissions.length ? (
            <div
              className={cn(
                "my-bonuses__cards__content__without__submissions",
                {
                  funBonus: gameBoxType === "funMission",
                }
              )}
            >
              {cardsDataWithoutSubmissions}
            </div>
          ) : null}
        </>
      );
    } else if (gameBoxType === "race" && raceLength) {
      content = <RaceBonus onClose={close} />;
    } else if (gameBoxType === "miniGames") {
      content = <MiniGames bgWidgetAudio={bgWidgetAudioRef.current} />;
    } /*else if (gameBoxType === "raffle") {
      const raffleMissions = mergeArrays(
        missions?.[gameBoxType],
        raffleTickets
      );
      content =
        raffleMissions?.length > 0 &&
        raffleMissions.map((raffleDrawData: any, index: Key) => (
          <div
            key={index}
            className={cn({
              "my-bonuses__cards__raffle": isDesktop,
              "my-bonuses__cards__separateRaffle":
                isDesktop && raffleMissions?.length > 1,
              landscape__raffle: isMobile && isLandscape,
            })}
          >
            <RaffleDrawTicketsInfoWidget data={raffleDrawData} />
          </div>
        ));
    }*/ else {
      content = <EmptyPage />;
    }

    return content;
  }, [missionData, gameBoxType, raceLength,/* raffleTickets,*/ isLandscape]);

  const items: TabsProps['items'] = [
    {
      key: 'sportWithTeam',
      label: t('currentSport'),
      children: cardsData(missionData),
    },
    {
      key: 'previousSport',
      label: t('previousSport'),
      children: cardsData(expiredMissionData?.data),
    },
  ];

  useEffect(() => {
    if (gameBoxType === 'sportWithTeam' && !expiredMissionData.fetched) {
      const now = new Date();
      const oneWeekPrev = new Date();
      oneWeekPrev.setDate(now.getDate() - 7);
      const params = {
        type: gameBoxType,
        expireTimeFrom: oneWeekPrev.toISOString(),
        expireTimeTo: now.toISOString(),
      }
      dispatch(requestExpiredProgressiveMissions(params));
    }
  }, [gameBoxType, expiredMissionData.fetched]);

  const handleChange = (value: string) => {
    if (value) {
      isSwitcherVisible && sessionStorage.setItem("switcherValue", value);
      changeTheme(value);
    }
  };

  const muteButton = useMemo(() => {
    return (
        <button onClick={toggleMute} className="toggle_mute">
          { isMuted ? <MutedOutlined /> : <SoundOutlined /> }
        </button>
    );
  }, [isMuted]);

  return (
    <>
      {isDesktop && <div className="my-bonuses__overlay" />}
      <div className={classes}>
        {isDesktop && isButtonVisible && (
          <div className="my-bonuses__title">
            <div className="my-bonuses__close-icon">
              <ReactSVG
                src="/static/images/close.svg"
                onClick={close}
                width={28}
              />
            </div>
          </div>
        )}
        <div
          className={cn({
            "my-bonuses__content": isDesktop,
          })}
        >
          {isDesktop && (
            <div className="my-bonuses__tags">
              <div className="my-bonuses__tags__demo">
                <div className="my-bonuses__tags__demo__content">
                  <img src="/static/images/box.png" width={70} alt="box" />
                  <p>
                    Game<span>Box</span>
                  </p>
                  { muteButton }
                </div>
                <div className="my-bonuses__tags__demo__demobtn">
                  <ReactSVG src="/static/images/play.svg" />
                  <Tooltip title={t("comingSoon")}>
                    <span>{t("tryDemo")}</span>
                  </Tooltip>
                </div>
                <img
                  src="/static/images/goldBox.png"
                  width={128}
                  alt="goldBox"
                />
              </div>
              <div className="my-bonuses__tags__title">
                <p />
                <img src="/static/images/world.png" width={25} alt="world" />
                <span>{t("categories")}</span>
              </div>
              <div className="my-bonuses__tags__data">
                {sortedCategories?.map(
                  (
                    value: {
                      tag: string;
                      isActive: boolean;
                      activeMissionsCount: number;
                    },
                    index
                  ) => {
                    return (
                      <BonusCategories
                        key={index}
                        tag={value?.tag}
                        isActive={value?.isActive}
                        activeMissionsCount={value?.activeMissionsCount}
                        selectedTag={gameBoxType}
                      />
                    );
                  }
                )}
                {isSwitcherVisible && (
                  <div className="select-theme">
                    <Select
                      defaultValue={
                        sessionStorage.getItem("switcherValue") || "dark"
                      }
                      popupClassName="dropdown-menu"
                      style={{ width: 175, height: 46 }}
                      onChange={handleChange}
                      options={[
                        { value: "dark", label: "Dark" },
                        { value: "lightGreen", label: "Light green" },
                        { value: "lightOrange", label: "Light orange" },
                      ]}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className={cn({
              "my-bonuses__cards": isDesktop,
            })}
          >
            {isMobile && (
              <div className="widget-mobile-header" ref={elementMobileHeaderRef}>
                <div className="my-bonuses__tags__demo__content">
                  <img src="/static/images/box.png" width={60} alt="box" />
                  <p>
                    Game<span>Box</span>
                  </p>
                  { muteButton }
                </div>
                <MissionTitleAndDescriptionWidget
                  mission={missionData?.[gameBoxType]?.[0]}
                  setIsOpen={setIsOpen}
                  isOpen={isOpen}
                  storedRaceValue={storedRaceValue}
                />
                {isSwitcherVisible && (
                  <div className="select-theme">
                    <Select
                      defaultValue={
                        sessionStorage.getItem("switcherValue") || "dark"
                      }
                      popupClassName="dropdown-menu"
                      style={{ width: 45, height: 26 }}
                      onChange={handleChange}
                      options={[
                        {
                          value: "dark",
                          label: (
                            <img
                              src="/static/images/switcher/1.png"
                              width={18}
                              alt="dark"
                            />
                          ),
                        },
                        {
                          value: "lightGreen",
                          label: (
                            <img
                              src="/static/images/switcher/3.png"
                              width={18}
                              alt="lightGreen"
                            />
                          ),
                        },
                        {
                          value: "lightOrange",
                          label: (
                            <img
                              src="/static/images/switcher/2.png"
                              width={18}
                              alt="lightOrange"
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                )}
                <ReactSVG
                  src="/static/images/close.svg"
                  onClick={close}
                  width={28}
                />
              </div>
            )}
            <Categories
              gameBoxType={gameBoxType}
              isWidget={isWidget}
              isLandscape={isLandscape}
              setIsOpen={setIsOpen}
              storedRaceValue={storedRaceValue}
            />
            {isDesktop && missionData?.[gameBoxType]?.length && (
              <>
                <MissionTitleAndDescriptionWidget
                  mission={missionData?.[gameBoxType]?.[0]}
                  setIsOpen={setIsOpen}
                  isOpen={isOpen}
                  storedRaceValue={storedRaceValue}
                />
              </>
            )}
            {loader ? (
              <Loader />
            ) : (
              <>
                <div
                  className={cn('my-bonuses_widget', {
                    "my-bonuses__cards__content": isDesktop,
                    "my-bonuses__mobile_scroll": isMobile,
                  })}
                >
                  {gameBoxType === 'sportWithTeam' ? (
                      <TabsSwitcher defaultActiveKey={gameBoxType} items={items} className={'sportWithTeam'} />
                  ) : cardsData(missionData)}
                </div>
                <Version />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyBonuses;
